import { useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";
import { Repeat, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/plant";

export function ManageSubscription({ user, config }) {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const poll = useRef(null);
  const sub = user.subscription || {};
  const target = sub.plan === "yearly" ? "monthly" : "yearly";
  const sym = config?.symbol || "£";
  const price = target === "yearly" ? `${sym}${config?.prices?.yearly || "49.99"}/yr` : `${sym}${config?.prices?.monthly || "4.99"}/mo`;
  const legacyCurrency = sub.currency && config?.currency && sub.currency !== config.currency;

  const confirmSwitch = async () => {
    try {
      const { data } = await api.post("/billing/switch/confirm");
      await refreshUser();
      data.switched ? toast.success(`Switched to ${target} — new price starts ${formatDate(sub.current_period_end)}`) : toast.info("Plan unchanged — the PayPal approval wasn't completed");
    } catch {
      toast.error("Could not confirm the switch");
    } finally {
      setBusy(false);
    }
  };

  const switchPlan = async () => {
    setBusy(true);
    const w = window.open("", "paypal-switch", "width=480,height=720");
    try {
      const { data } = await api.post("/billing/switch", { plan: target });
      if (!w) { window.location.href = data.approve_url; return; }
      w.location.href = data.approve_url;
      clearInterval(poll.current);
      poll.current = setInterval(() => {
        if (w.closed) { clearInterval(poll.current); confirmSwitch(); }
      }, 800);
    } catch (e) {
      w?.close();
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not start the switch");
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-border pt-5 flex flex-col sm:flex-row items-center justify-center gap-2" data-testid="manage-subscription">
      {sub.pending_plan && (
        <p className="w-full text-xs text-muted-foreground mb-1" data-testid="pending-switch">Switch to {sub.pending_plan} is waiting for PayPal approval.</p>
      )}
      {legacyCurrency ? (
        <p className="w-full text-xs text-muted-foreground mb-1" data-testid="legacy-currency-note">
          Your subscription is billed in {sub.currency}. To change plans, cancel it and subscribe again in {config.currency} once it ends.
        </p>
      ) : (
        <Button onClick={switchPlan} disabled={busy} variant="secondary" className="rounded-xl" data-testid="switch-plan-button">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Repeat className="h-4 w-4 mr-1.5" />}
          Switch to {target === "yearly" ? "Yearly" : "Monthly"} · {price}
        </Button>
      )}
      <Button variant="ghost" disabled={busy} onClick={() => setConfirmOpen(true)} className="rounded-xl text-destructive hover:text-destructive" data-testid="cancel-subscription-button">Cancel subscription</Button>
      <CancelSubscriptionDialog open={confirmOpen} onOpenChange={setConfirmOpen} onBusy={setBusy} />
    </div>
  );
}
